import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Users, DollarSign, Image, TrendingUp, Building2, 
  User, LogOut, RefreshCw, ChevronLeft, ChevronRight, AlertTriangle, Zap,
  BarChart3, Trophy, Target, Activity, Download, Trash2, Eye, X, Droplets, Settings, Tag, Copy, Check, Link2, Ban, AlertCircle
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, loading: authLoading } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [growthData, setGrowthData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [watermark, setWatermark] = useState(null);
  const [watermarkSaving, setWatermarkSaving] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoSelectedPlan, setPromoSelectedPlan] = useState('');
  const [promoNote, setPromoNote] = useState('');
  const [promoDuration, setPromoDuration] = useState(1);
  const [promoGenerating, setPromoGenerating] = useState(false);
  const [promoGenerated, setPromoGenerated] = useState(null);
  const [promoCopied, setPromoCopied] = useState(false);
  const [geminiErrors, setGeminiErrors] = useState(null);
  const [geminiErrorsLoading, setGeminiErrorsLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Allow admin access if user_type is admin or if still loading
    if (authLoading) return;
    
    // Double-check localStorage for admin status (in case context hasn't updated yet)
    const isAdminFromStorage = localStorage.getItem('isAdmin') === 'true';
    const tokenFromStorage = localStorage.getItem('token');
    
    if (isAdminFromStorage && tokenFromStorage) {
      // Admin is logged in via localStorage, proceed with data fetch
      fetchData();
      fetchAiUsageAlert();
    } else if (!user || user.user_type !== 'admin') {
      navigate('/admin/login');
    } else {
      fetchData();
      fetchAiUsageAlert(); // Check for alerts on load
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'revenue') {
      fetchCampaignAnalytics();
    } else if (activeTab === 'ai-usage') {
      fetchAiUsage();
    } else if (activeTab === 'growth') {
      fetchGrowthData();
    } else if (activeTab === 'watermark') {
      fetchWatermarkSettings();
    } else if (activeTab === 'promos') {
      fetchPromoCodes();
    } else if (activeTab === 'gemini-errors') {
      fetchGeminiErrors();
    }
  }, [activeTab, page, filterType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAiUsage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/ai-usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAiUsage(data);
      }
    } catch (err) {
      console.error('Error fetching AI usage:', err);
    }
  };

  const fetchGeminiErrors = async () => {
    setGeminiErrorsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/gemini-errors/recent?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGeminiErrors(data);
      }
    } catch (err) {
      console.error('Error fetching Gemini errors:', err);
    } finally {
      setGeminiErrorsLoading(false);
    }
  };

  const fetchAiUsageAlert = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/ai-usage-alert`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.has_alert) {
          setAiUsage(prev => ({ ...prev, alert: data }));
        }
      }
    } catch (err) {
      console.error('Error fetching AI usage alert:', err);
    }
  };

  const fetchCampaignAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/campaign-analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCampaignAnalytics(data);
      }
    } catch (err) {
      console.error('Error fetching campaign analytics:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filterType) params.append('user_type', filterType);
      
      const res = await fetch(`${API_URL}/api/admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setTotalPages(data.pages);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchGrowthData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/growth-analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGrowthData(data);
      }
    } catch (err) {
      console.error('Error fetching growth data:', err);
    }
  };

  const fetchWatermarkSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/watermark`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const s = data.settings;
        setWatermark({
          enabled: s.enabled,
          text: s.text || 'VirtualFit',
          mode: s.type?.includes('tiled') ? 'tiled' : s.type === 'text' ? 'single' : 'tiled',
          opacity: (s.opacity || 25) / 100,
          font_size: s.font_size || 48,
          color: s.font_color || 'white',
          rotation: s.rotation ?? -30,
          tile_spacing_x: s.tile_spacing || 200,
          tile_spacing_y: s.tile_spacing || 200,
          position: s.position || 'south_east'
        });
      }
    } catch (err) {
      console.error('Error fetching watermark:', err);
    }
  };

  const saveWatermarkSettings = async () => {
    setWatermarkSaving(true);
    try {
      // Map frontend state to backend model
      const payload = {
        enabled: watermark.enabled,
        type: watermark.mode === 'tiled' ? 'tiled_text' : watermark.mode === 'diagonal' ? 'tiled_text' : 'text',
        text: watermark.text || 'VirtualFit',
        position: watermark.position || 'south_east',
        opacity: Math.round((watermark.opacity || 0.15) * 100),
        font_size: watermark.font_size || 36,
        font_color: watermark.color || 'white',
        tile_spacing: watermark.tile_spacing_x || 200,
        rotation: watermark.rotation || -30
      };
      const res = await fetch(`${API_URL}/api/admin/watermark`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        // Map backend response back to frontend format
        const s = data.settings;
        setWatermark({
          enabled: s.enabled,
          text: s.text,
          mode: s.type?.includes('tiled') ? 'tiled' : 'single',
          opacity: (s.opacity || 25) / 100,
          font_size: s.font_size,
          color: s.font_color,
          rotation: s.rotation,
          tile_spacing_x: s.tile_spacing,
          tile_spacing_y: s.tile_spacing,
          position: s.position
        });
        alert('Watermark settings saved!');
      } else {
        alert('Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving watermark:', err);
      alert('Error saving settings');
    }
    setWatermarkSaving(false);
  };

  // Promo code functions
  const PLAN_OPTIONS = [
    { id: 'brand_growth', name: 'Brand Growth', price: 99 },
    { id: 'brand_starter', name: 'Brand Starter', price: 199 },
    { id: 'brand_enterprise', name: 'Brand Enterprise', price: 399 },
    { id: 'influencer_starter', name: 'Influencer Starter', price: 19 },
    { id: 'influencer_pro', name: 'Influencer Pro', price: 79.99 },
    { id: 'personal', name: 'Personal', price: 9 },
  ];

  const fetchPromoCodes = async () => {
    setPromoLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/promo-codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPromoCodes(data.promo_codes || []);
      }
    } catch (err) {
      console.error('Error fetching promo codes:', err);
    }
    setPromoLoading(false);
  };

  const generatePromoCode = async () => {
    if (!promoSelectedPlan) return;
    setPromoGenerating(true);
    setPromoGenerated(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/promo-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: promoSelectedPlan,
          note: promoNote,
          duration_months: promoDuration,
          // Pass the admin's current origin so the generated link matches
          // this environment (staging → staging URL, prod → prod URL).
          origin_url: window.location.origin,
        })
      });
      if (res.ok) {
        const data = await res.json();
        setPromoGenerated(data);
        setPromoNote('');
        fetchPromoCodes(); // Refresh list
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to generate promo code');
      }
    } catch (err) {
      console.error('Error generating promo code:', err);
      alert('Error generating promo code');
    }
    setPromoGenerating(false);
  };

  const deactivatePromoCode = async (promoId) => {
    if (!window.confirm('Deactivate this promo code? It will no longer be usable.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/promo-codes/${promoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchPromoCodes();
      } else {
        alert('Failed to deactivate promo code');
      }
    } catch (err) {
      alert('Error deactivating promo code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setPromoCopied(true);
    setTimeout(() => setPromoCopied(false), 2000);
  };


  // Export data as JSON file download
  const handleExport = async (exportType) => {
    setExportLoading(true);
    try {
      const endpoint = exportType === 'all' ? '/api/admin/export/all' : `/api/admin/export/${exportType}`;
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `virtualfit_${exportType}_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExportLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUser._id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setShowDeleteModal(false);
        setSelectedUser(null);
        fetchUsers(); // Refresh user list
        fetchData(); // Refresh analytics
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading || !analytics) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-rose-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin" className="text-xl font-bold text-white">VirtualFit</Link>
            <span className="px-2 py-1 bg-rose-500 text-white text-xs rounded-full">Admin</span>
          </div>
          <button onClick={logout} className="text-gray-400 hover:text-white flex items-center">
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 font-medium whitespace-nowrap ${activeTab === 'overview' ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-400'}`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('revenue')}
              className={`py-4 font-medium whitespace-nowrap ${activeTab === 'revenue' ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-400'}`}
            >
              Platform Revenue
            </button>
            <button
              onClick={() => setActiveTab('ai-usage')}
              className={`py-4 font-medium whitespace-nowrap flex items-center ${activeTab === 'ai-usage' ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-400'}`}
            >
              AI Costs
              {aiUsage?.alert?.has_alert && (
                <span className={`ml-2 w-2 h-2 rounded-full animate-pulse ${aiUsage.alert.level === 'critical' ? 'bg-red-500' : 'bg-yellow-500'}`} />
              )}
            </button>
            <button
              onClick={() => setActiveTab('growth')}
              className={`py-4 font-medium whitespace-nowrap flex items-center ${activeTab === 'growth' ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-400'}`}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Growth
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 font-medium whitespace-nowrap ${activeTab === 'users' ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-400'}`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('watermark')}
              className={`py-4 font-medium whitespace-nowrap flex items-center ${activeTab === 'watermark' ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-400'}`}
            >
              <Droplets className="w-4 h-4 mr-2" />
              Watermark
            </button>
            <button
              onClick={() => setActiveTab('promos')}
              className={`py-4 font-medium whitespace-nowrap flex items-center ${activeTab === 'promos' ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-400'}`}
            >
              <Tag className="w-4 h-4 mr-2" />
              Promo Codes
            </button>
            <button
              onClick={() => setActiveTab('gemini-errors')}
              data-testid="admin-tab-gemini-errors"
              className={`py-4 font-medium whitespace-nowrap flex items-center ${activeTab === 'gemini-errors' ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-400'}`}
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              Gemini Errors
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-blue-400" />
                  <span className="text-xs text-gray-500">Total Users</span>
                </div>
                <p className="text-3xl font-bold text-white">{analytics.users.total}</p>
                <p className="text-sm text-green-400">{analytics.users.active} active</p>
                <p className="text-xs text-gray-500 mt-1">+{analytics.users.new_7d || 0} this week</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <DollarSign className="w-8 h-8 text-green-400" />
                  <span className="text-xs text-gray-500">MRR</span>
                </div>
                <p className="text-3xl font-bold text-white">${(analytics.revenue.mrr || 0).toFixed(2)}</p>
                <p className="text-sm text-gray-400">Total: ${analytics.revenue.total.toFixed(2)}</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Image className="w-8 h-8 text-purple-400" />
                  <span className="text-xs text-gray-500">Images (30d)</span>
                </div>
                <p className="text-3xl font-bold text-white">{analytics.usage.images_30d || analytics.usage.images_generated_30d || 0}</p>
                <p className="text-sm text-gray-400">Cost: ${analytics.usage.estimated_cost_30d || analytics.usage.estimated_cost || 0}</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="w-8 h-8 text-rose-400" />
                  <span className="text-xs text-gray-500">Conversion</span>
                </div>
                <p className="text-3xl font-bold text-white">{analytics.metrics?.conversion_rate || analytics.roi?.percentage || 0}%</p>
                <p className="text-sm text-gray-400">Churn: {analytics.metrics?.churn_rate || 0}%</p>
              </div>
            </div>

            {/* Profit Card + Commission */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-r from-green-900 to-emerald-900 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-300 text-sm mb-1">30-Day Profit</p>
                    <p className="text-3xl font-bold text-white">${(analytics.metrics?.profit_30d || analytics.roi?.profit || 0).toFixed(2)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-green-300 text-sm mb-1">ARPU</p>
                    <p className="text-2xl font-bold text-white">${(analytics.revenue.arpu || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-amber-900 to-orange-900 rounded-xl p-6" data-testid="commission-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-300 text-sm mb-1">Commission Revenue (15%)</p>
                    <p className="text-3xl font-bold text-white">${analytics.revenue?.commission?.total?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-amber-300 text-sm mb-1">Last 30 Days</p>
                    <p className="text-2xl font-bold text-white">${analytics.revenue?.commission?.last_30d?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-amber-400">{analytics.revenue?.commission?.count || 0} transactions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* User Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Users by Type</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Building2 className="w-5 h-5 text-blue-400 mr-3" />
                      <span className="text-gray-300">Brands</span>
                    </div>
                    <span className="text-white font-semibold">{analytics.users.brands}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="w-5 h-5 text-purple-400 mr-3" />
                      <span className="text-gray-300">Influencers</span>
                    </div>
                    <span className="text-white font-semibold">{analytics.users.influencers}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-rose-400 mr-3" />
                      <span className="text-gray-300">Personal</span>
                    </div>
                    <span className="text-white font-semibold">{analytics.users.personal}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Plans Breakdown</h3>
                <div className="space-y-3">
                  {Object.entries(analytics.plan_breakdown).map(([planId, planData]) => (
                    <div key={planId} className="flex items-center justify-between">
                      <span className="text-gray-300 text-sm">{planData?.name || planId.replace(/_/g, ' ')}</span>
                      <div className="text-right">
                        <span className="text-white font-semibold">{typeof planData === 'object' ? planData?.count : planData}</span>
                        {typeof planData === 'object' && planData?.revenue > 0 && (
                          <span className="text-green-400 text-xs ml-2">${planData.revenue}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'revenue' && (
          <>
            {/* Platform Revenue Overview */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <DollarSign className="w-8 h-8 text-green-400" />
                  <span className="text-xs text-gray-500">Platform Fees (15%)</span>
                </div>
                <p className="text-3xl font-bold text-white">${campaignAnalytics?.platform_revenue?.total_platform_fees?.toFixed(2) || '0.00'}</p>
                <p className="text-sm text-green-400">From content approvals</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Building2 className="w-8 h-8 text-blue-400" />
                  <span className="text-xs text-gray-500">Campaigns</span>
                </div>
                <p className="text-3xl font-bold text-white">{campaignAnalytics?.campaigns?.active || 0}</p>
                <p className="text-sm text-gray-400">Total: {campaignAnalytics?.campaigns?.total || 0}</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Image className="w-8 h-8 text-purple-400" />
                  <span className="text-xs text-gray-500">Content</span>
                </div>
                <p className="text-3xl font-bold text-white">{campaignAnalytics?.content?.approved || 0}</p>
                <p className="text-sm text-gray-400">Pending: {campaignAnalytics?.content?.pending || 0}</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="w-8 h-8 text-rose-400" />
                  <span className="text-xs text-gray-500">Payments Flow</span>
                </div>
                <p className="text-xl font-bold text-white">${campaignAnalytics?.payments?.total_deposits?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-gray-400">Deposits</p>
                <p className="text-lg font-bold text-green-400 mt-1">${campaignAnalytics?.payments?.total_influencer_payments?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-gray-400">Influencer Payouts</p>
              </div>
            </div>

            {/* Recent Platform Fees */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Recent Platform Fees (15% Commission)</h3>
              {campaignAnalytics?.platform_revenue?.recent_fees?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="text-left text-gray-300 px-4 py-3 text-sm">Date</th>
                        <th className="text-left text-gray-300 px-4 py-3 text-sm">Amount</th>
                        <th className="text-left text-gray-300 px-4 py-3 text-sm">Description</th>
                        <th className="text-left text-gray-300 px-4 py-3 text-sm">Campaign ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {campaignAnalytics.platform_revenue.recent_fees.map((fee) => (
                        <tr key={fee.id} className="hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-gray-300 text-sm">
                            {new Date(fee.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-green-400 font-semibold">${fee.amount?.toFixed(2)}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-300 text-sm max-w-xs truncate">
                            {fee.description}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs font-mono">
                            {fee.campaign_id?.substring(0, 8)}...
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No platform fees recorded yet. Fees are collected when brands approve influencer content.</p>
              )}
            </div>

            {/* Note about Stripe */}
            <div className="mt-6 bg-gray-700/50 rounded-xl p-4 border border-gray-600">
              <h4 className="text-white font-semibold mb-2">💰 How Platform Fees Work</h4>
              <div className="text-gray-400 text-sm space-y-2">
                <p>
                  <span className="text-green-400">1. Brand Deposits:</span> Brands deposit funds via Stripe Checkout → funds go to VirtualFit's Stripe account
                </p>
                <p>
                  <span className="text-blue-400">2. Content Approval:</span> When brand approves content → 85% goes to influencer's balance, 15% stays as platform fee
                </p>
                <p>
                  <span className="text-purple-400">3. Influencer Payout:</span> Influencers withdraw via Stripe Connect Express → funds transfer from VirtualFit to their bank
                </p>
                <p>
                  <span className="text-rose-400">4. Platform Revenue:</span> The 15% platform fee remains in VirtualFit's Stripe account as revenue
                </p>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-600">
                <p className="text-yellow-400 text-sm">
                  ⚠️ <strong>TEST MODE:</strong> All transactions use Stripe test keys. No real money is moved.
                  In production, platform fees would accumulate in your main Stripe balance.
                </p>
              </div>
            </div>
          </>
        )}

        {activeTab === 'ai-usage' && (
          <>
            {/* AI Usage Alert Banner */}
            {aiUsage?.alert?.level !== 'normal' && aiUsage?.alert?.message && (
              <div className={`mb-6 p-4 rounded-xl border ${
                aiUsage.alert.level === 'critical' 
                  ? 'bg-red-900/30 border-red-500 text-red-300' 
                  : 'bg-yellow-900/30 border-yellow-500 text-yellow-300'
              }`}>
                <div className="flex items-center">
                  <AlertTriangle className="w-6 h-6 mr-3" />
                  <div>
                    <p className="font-semibold">{aiUsage.alert.message}</p>
                    <p className="text-sm mt-1 opacity-80">
                      Go to Profile → Universal Key → Add Balance to top up.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* AI Usage Stats Grid */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Zap className="w-8 h-8 text-yellow-400" />
                  <span className="text-xs text-gray-500">This Month</span>
                </div>
                <p className="text-3xl font-bold text-white">{aiUsage?.this_month?.total_generations || 0}</p>
                <p className="text-sm text-gray-400">Images Generated</p>
                <p className="text-xs text-red-400 mt-1">{aiUsage?.this_month?.failed_generations || 0} failed</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <DollarSign className="w-8 h-8 text-green-400" />
                  <span className="text-xs text-gray-500">Monthly Cost</span>
                </div>
                <p className="text-3xl font-bold text-white">${aiUsage?.this_month?.estimated_cost?.toFixed(2) || '0.00'}</p>
                <p className="text-sm text-purple-400 font-medium">{aiUsage?.this_month?.credits_used?.toFixed(2) || '0.00'} credits</p>
                <div className="mt-2 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${
                      (aiUsage?.this_month?.estimated_cost || 0) >= 100 ? 'bg-red-500' :
                      (aiUsage?.this_month?.estimated_cost || 0) >= 50 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(((aiUsage?.this_month?.estimated_cost || 0) / 100) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Budget: $100 (500 credits)</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Image className="w-8 h-8 text-purple-400" />
                  <span className="text-xs text-gray-500">All Time</span>
                </div>
                <p className="text-3xl font-bold text-white">{aiUsage?.all_time?.total_generations || 0}</p>
                <p className="text-sm text-gray-400">Total images</p>
                <p className="text-xs text-green-400 mt-1">
                  ${aiUsage?.all_time?.estimated_cost?.toFixed(2) || '0.00'} / {aiUsage?.all_time?.credits_used?.toFixed(2) || '0.00'} credits
                </p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-blue-400" />
                  <span className="text-xs text-gray-500">Usage Split</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Free Trial</span>
                    <span className="text-white font-semibold">{aiUsage?.this_month?.free_trial_usage || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Subscribed</span>
                    <span className="text-white font-semibold">{aiUsage?.this_month?.subscribed_usage || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Breakdown Info */}
            <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-xl p-6 mb-8 border border-purple-500/30">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-yellow-400" />
                AI Cost & Credits Breakdown
              </h3>
              <div className="grid md:grid-cols-4 gap-4 text-sm">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-purple-300 font-medium">Per Image</p>
                  <p className="text-white text-xl font-bold">~$0.03</p>
                  <p className="text-purple-400 text-sm">0.15 credits</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-blue-300 font-medium">Conversion Rate</p>
                  <p className="text-white text-xl font-bold">$1 = 5 credits</p>
                  <p className="text-gray-400 text-xs">Emergent Universal Key</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-yellow-300 font-medium">Warning Threshold</p>
                  <p className="text-yellow-400 text-xl font-bold">$50</p>
                  <p className="text-gray-400 text-xs">250 credits</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-red-300 font-medium">Critical Threshold</p>
                  <p className="text-red-400 text-xl font-bold">$100</p>
                  <p className="text-gray-400 text-xs">500 credits</p>
                </div>
              </div>
              <p className="text-gray-400 text-xs mt-4">
                📧 Admin email alerts are automatically sent to support@virtualfit.work when thresholds are crossed.
              </p>
            </div>

            {/* Recent Generations Table */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Recent AI Generations</h3>
              {aiUsage?.recent_generations?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="text-left text-gray-300 px-4 py-3 text-sm">Date</th>
                        <th className="text-left text-gray-300 px-4 py-3 text-sm">User</th>
                        <th className="text-left text-gray-300 px-4 py-3 text-sm">Type</th>
                        <th className="text-left text-gray-300 px-4 py-3 text-sm">Status</th>
                        <th className="text-left text-gray-300 px-4 py-3 text-sm">Cost / Credits</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {aiUsage.recent_generations.slice(0, 20).map((gen, idx) => (
                        <tr key={idx} className="hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-gray-300 text-sm">
                            {gen.created_at ? new Date(gen.created_at).toLocaleString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-300 text-sm max-w-[150px] truncate">
                            {gen.user_id?.toString().startsWith('free_trial_') 
                              ? <span className="text-amber-400">Free Trial</span>
                              : gen.user_email || gen.user_id?.toString().substring(0, 8) + '...'
                            }
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400">
                              {gen.generation_type || gen.type || 'try-on'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              gen.success ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {gen.success ? 'Success' : 'Failed'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-green-400 font-medium">${gen.estimated_cost?.toFixed(2) || '0.00'}</span>
                            <span className="text-purple-400 text-xs ml-1">({gen.credits_used?.toFixed(2) || (gen.estimated_cost * 5)?.toFixed(2) || '0.00'})</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Zap className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500">No AI generations recorded yet.</p>
                  <p className="text-gray-600 text-sm mt-1">Usage will appear here when users generate virtual try-on images.</p>
                </div>
              )}
            </div>

            {/* Tip Card */}
            <div className="mt-6 bg-gray-700/50 rounded-xl p-4 border border-gray-600">
              <h4 className="text-white font-semibold mb-2">💡 Cost Management Tips</h4>
              <ul className="text-gray-400 text-sm space-y-1 list-disc list-inside">
                <li>Free trial users get limited generations to control costs</li>
                <li>Subscribed users have plan-based limits to prevent abuse</li>
                <li>Monitor this dashboard regularly to avoid unexpected charges</li>
                <li>Top up your Universal Key before hitting critical threshold</li>
              </ul>
            </div>
          </>
        )}

        {activeTab === 'growth' && (
          <>
            {/* Growth Stats Grid */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Activity className="w-8 h-8 text-green-400" />
                  <span className="text-xs text-gray-500">Weekly Active</span>
                </div>
                <p className="text-3xl font-bold text-white">{growthData?.weekly_active_users || 0}</p>
                <p className="text-sm text-gray-400">Active this week</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Target className="w-8 h-8 text-blue-400" />
                  <span className="text-xs text-gray-500">Collab Success</span>
                </div>
                <p className="text-3xl font-bold text-white">{growthData?.collaboration_success_rate || 0}%</p>
                <p className="text-sm text-gray-400">Content approved</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="w-8 h-8 text-purple-400" />
                  <span className="text-xs text-gray-500">Campaign Rate</span>
                </div>
                <p className="text-3xl font-bold text-white">{growthData?.campaign_completion_rate || 0}%</p>
                <p className="text-sm text-gray-400">Completed campaigns</p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Image className="w-8 h-8 text-rose-400" />
                  <span className="text-xs text-gray-500">Content Stats</span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-green-400">Approved</span>
                    <span className="text-white">{growthData?.content_stats?.approved || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-yellow-400">Pending</span>
                    <span className="text-white">{growthData?.content_stats?.pending || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-400">Rejected</span>
                    <span className="text-white">{growthData?.content_stats?.rejected || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Signup Trend Chart */}
            <div className="bg-gray-800 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-400" />
                Daily Signups (Last 30 Days)
              </h3>
              {growthData?.daily_signups?.length > 0 ? (
                <div className="h-48 flex items-end gap-1 overflow-x-auto pb-4">
                  {growthData.daily_signups.map((day, idx) => {
                    const maxCount = Math.max(...growthData.daily_signups.map(d => d.count), 1);
                    const height = (day.count / maxCount) * 100;
                    return (
                      <div key={idx} className="flex flex-col items-center min-w-[20px] group">
                        <div 
                          className="w-4 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t hover:from-blue-500 hover:to-blue-300 transition-all cursor-pointer relative"
                          style={{ height: `${Math.max(height, 4)}%` }}
                        >
                          <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs text-white bg-gray-700 px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap">
                            {day.count} signups
                          </span>
                        </div>
                        {idx % 5 === 0 && (
                          <span className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-left">
                            {day.date.slice(5)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No signup data available</p>
              )}
            </div>

            {/* Top Performers */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Top Brands */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
                  Top Performing Brands
                </h3>
                {growthData?.top_brands?.length > 0 ? (
                  <div className="space-y-3">
                    {growthData.top_brands.map((brand, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                        <div className="flex items-center">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3 ${
                            idx === 0 ? 'bg-yellow-500 text-black' : 
                            idx === 1 ? 'bg-gray-400 text-black' : 
                            idx === 2 ? 'bg-amber-700 text-white' : 'bg-gray-600 text-white'
                          }`}>
                            {idx + 1}
                          </span>
                          <div>
                            <p className="text-white font-medium">{brand.name}</p>
                            <p className="text-gray-400 text-xs">@{brand.handle}</p>
                          </div>
                        </div>
                        <span className="text-green-400 font-semibold">{brand.approved_content} content</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No brand activity yet</p>
                )}
              </div>

              {/* Top Influencers */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-purple-400" />
                  Top Earning Influencers
                </h3>
                {growthData?.top_influencers?.length > 0 ? (
                  <div className="space-y-3">
                    {growthData.top_influencers.map((influencer, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                        <div className="flex items-center">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3 ${
                            idx === 0 ? 'bg-yellow-500 text-black' : 
                            idx === 1 ? 'bg-gray-400 text-black' : 
                            idx === 2 ? 'bg-amber-700 text-white' : 'bg-gray-600 text-white'
                          }`}>
                            {idx + 1}
                          </span>
                          <div>
                            <p className="text-white font-medium">{influencer.name}</p>
                            <p className="text-gray-400 text-xs">@{influencer.handle}</p>
                          </div>
                        </div>
                        <span className="text-purple-400 font-semibold">{influencer.approved_content} content</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No influencer activity yet</p>
                )}
              </div>
            </div>

            {/* Plan Signups in Last 30 Days */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Plan Signups (Last 30 Days)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {growthData?.plan_signups_30d && Object.entries(growthData.plan_signups_30d).map(([planId, count]) => (
                  <div key={planId} className="bg-gray-700/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-white">{count}</p>
                    <p className="text-xs text-gray-400 mt-1">{planId.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'users' && (
          <>
            {/* Filters and Export Buttons */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <select
                  value={filterType}
                  onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
                  className="bg-gray-800 text-white border border-gray-700 rounded-lg px-4 py-2"
                >
                  <option value="">All Types</option>
                  <option value="brand">Brands</option>
                  <option value="influencer">Influencers</option>
                  <option value="personal">Personal</option>
                </select>
              </div>
              
              {/* Export Buttons */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleExport('users')}
                  disabled={exportLoading}
                  className="flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm disabled:opacity-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Users
                </button>
                <button
                  onClick={() => handleExport('payments')}
                  disabled={exportLoading}
                  className="flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm disabled:opacity-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Payments
                </button>
                <button
                  onClick={() => handleExport('all')}
                  disabled={exportLoading}
                  className="flex items-center px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm disabled:opacity-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export All Data
                </button>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-gray-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="text-left text-gray-300 px-6 py-4 text-sm">User</th>
                    <th className="text-left text-gray-300 px-6 py-4 text-sm">Type</th>
                    <th className="text-left text-gray-300 px-6 py-4 text-sm">Plan</th>
                    <th className="text-left text-gray-300 px-6 py-4 text-sm">Status</th>
                    <th className="text-left text-gray-300 px-6 py-4 text-sm">Usage</th>
                    <th className="text-left text-gray-300 px-6 py-4 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {users.map((u) => (
                    <tr key={u._id} className="hover:bg-gray-700/50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-medium">{u.name}</p>
                          <p className="text-gray-400 text-sm">@{u.handle}</p>
                          <p className="text-gray-500 text-xs">{u.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          u.user_type === 'brand' ? 'bg-blue-500/20 text-blue-400' :
                          u.user_type === 'influencer' ? 'bg-purple-500/20 text-purple-400' :
                          u.user_type === 'admin' ? 'bg-red-500/20 text-red-400' :
                          'bg-rose-500/20 text-rose-400'
                        }`}>
                          {u.user_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300 text-sm">
                        {u.plan_id?.replace(/_/g, ' ') || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          u.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {u.subscription_status || 'pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300 text-sm">
                        {u.images_used_this_month || 0} / {u.images_limit || 0}
                      </td>
                      <td className="px-6 py-4">
                        {u.user_type !== 'admin' && (
                          <button
                            onClick={() => { setSelectedUser(u); setShowDeleteModal(true); }}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700">
                <p className="text-gray-400 text-sm">Page {page} of {totalPages}</p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 bg-gray-700 rounded-lg text-gray-300 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 bg-gray-700 rounded-lg text-gray-300 disabled:opacity-50"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Delete User Modal */}
        {showDeleteModal && selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Delete User</h3>
                <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                <p className="text-red-400 font-medium">⚠️ This action cannot be undone!</p>
                <p className="text-gray-400 text-sm mt-1">
                  All user data will be permanently deleted including: profile, portfolios, collaborations, messages, campaigns, and payment history.
                </p>
              </div>
              
              <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
                <p className="text-white font-medium">{selectedUser.name}</p>
                <p className="text-gray-400 text-sm">{selectedUser.email}</p>
                <p className="text-gray-500 text-xs">@{selectedUser.handle} • {selectedUser.user_type}</p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {deleteLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete User
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'watermark' && (
          <div className="space-y-6" data-testid="watermark-settings">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <Droplets className="w-6 h-6 mr-3 text-rose-400" />
                Watermark Settings
              </h2>
              <button
                onClick={saveWatermarkSettings}
                disabled={watermarkSaving}
                className="px-6 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-medium hover:shadow-lg disabled:opacity-50 flex items-center"
                data-testid="save-watermark-btn"
              >
                {watermarkSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
                {watermarkSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            {watermark ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* General Settings */}
                <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4">General</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-gray-300 text-sm">Enable Watermark</label>
                      <button
                        onClick={() => setWatermark(w => ({ ...w, enabled: !w.enabled }))}
                        className={`relative w-11 h-6 rounded-full transition-colors ${watermark.enabled ? 'bg-rose-500' : 'bg-gray-600'}`}
                        data-testid="watermark-toggle"
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${watermark.enabled ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>
                    <div>
                      <label className="text-gray-300 text-sm block mb-1">Watermark Text</label>
                      <input
                        type="text"
                        value={watermark.text || ''}
                        onChange={e => setWatermark(w => ({ ...w, text: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                        data-testid="watermark-text-input"
                      />
                    </div>
                    <div>
                      <label className="text-gray-300 text-sm block mb-1">Mode</label>
                      <select
                        value={watermark.mode || 'tiled'}
                        onChange={e => setWatermark(w => ({ ...w, mode: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                        data-testid="watermark-mode-select"
                      >
                        <option value="tiled">Tiled (repeating pattern)</option>
                        <option value="single">Single (one watermark)</option>
                        <option value="diagonal">Diagonal strip</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Appearance */}
                <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4">Appearance</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-gray-300 text-sm block mb-1">Opacity ({Math.round((watermark.opacity || 0.15) * 100)}%)</label>
                      <input
                        type="range"
                        min="0.05"
                        max="0.5"
                        step="0.05"
                        value={watermark.opacity || 0.15}
                        onChange={e => setWatermark(w => ({ ...w, opacity: parseFloat(e.target.value) }))}
                        className="w-full accent-rose-500"
                        data-testid="watermark-opacity-slider"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Subtle</span><span>Bold</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-gray-300 text-sm block mb-1">Font Size ({watermark.font_size || 36}px)</label>
                      <input
                        type="range"
                        min="16"
                        max="72"
                        step="4"
                        value={watermark.font_size || 36}
                        onChange={e => setWatermark(w => ({ ...w, font_size: parseInt(e.target.value) }))}
                        className="w-full accent-rose-500"
                        data-testid="watermark-fontsize-slider"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Small</span><span>Large</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-gray-300 text-sm block mb-1">Color</label>
                      <select
                        value={watermark.color || 'white'}
                        onChange={e => setWatermark(w => ({ ...w, color: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                        data-testid="watermark-color-select"
                      >
                        <option value="white">White</option>
                        <option value="black">Black</option>
                        <option value="gray">Gray</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-gray-300 text-sm block mb-1">Rotation ({watermark.rotation || -30}°)</label>
                      <input
                        type="range"
                        min="-45"
                        max="0"
                        step="5"
                        value={watermark.rotation || -30}
                        onChange={e => setWatermark(w => ({ ...w, rotation: parseInt(e.target.value) }))}
                        className="w-full accent-rose-500"
                        data-testid="watermark-rotation-slider"
                      />
                    </div>
                  </div>
                </div>

                {/* Tiled Settings */}
                {watermark.mode === 'tiled' && (
                  <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-4">Tiled Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-gray-300 text-sm block mb-1">Horizontal Spacing ({watermark.tile_spacing_x || 200}px)</label>
                        <input
                          type="range" min="100" max="400" step="20"
                          value={watermark.tile_spacing_x || 200}
                          onChange={e => setWatermark(w => ({ ...w, tile_spacing_x: parseInt(e.target.value) }))}
                          className="w-full accent-rose-500"
                        />
                      </div>
                      <div>
                        <label className="text-gray-300 text-sm block mb-1">Vertical Spacing ({watermark.tile_spacing_y || 200}px)</label>
                        <input
                          type="range" min="100" max="400" step="20"
                          value={watermark.tile_spacing_y || 200}
                          onChange={e => setWatermark(w => ({ ...w, tile_spacing_y: parseInt(e.target.value) }))}
                          className="w-full accent-rose-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview */}
                <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4">Preview</h3>
                  <div className="relative bg-gradient-to-br from-sky-300 to-indigo-300 rounded-xl h-48 overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Image className="w-16 h-16 text-white/40" />
                    </div>
                    {watermark.enabled && (
                      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                        {watermark.mode === 'tiled' ? (
                          <div className="absolute inset-0" style={{
                            backgroundImage: `repeating-linear-gradient(${watermark.rotation || -30}deg, transparent, transparent 40px, ${watermark.color === 'black' ? 'rgba(0,0,0,' : 'rgba(255,255,255,'}${watermark.opacity || 0.15}) 40px, ${watermark.color === 'black' ? 'rgba(0,0,0,' : 'rgba(255,255,255,'}${watermark.opacity || 0.15}) 41px)`,
                          }}>
                            <div className="flex flex-wrap gap-8 p-4" style={{ transform: `rotate(${watermark.rotation || -30}deg)`, transformOrigin: 'center' }}>
                              {Array.from({ length: 12 }).map((_, i) => (
                                <span key={i} style={{ fontSize: `${Math.max(12, (watermark.font_size || 36) * 0.4)}px`, color: watermark.color || 'white', opacity: watermark.opacity || 0.15, fontWeight: 'bold' }}>
                                  {watermark.text || 'VirtualFit'}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <span style={{
                            fontSize: `${Math.max(14, (watermark.font_size || 36) * 0.5)}px`,
                            color: watermark.color || 'white',
                            opacity: watermark.opacity || 0.15,
                            fontWeight: 'bold',
                            transform: `rotate(${watermark.rotation || -30}deg)`
                          }}>
                            {watermark.text || 'VirtualFit'}
                          </span>
                        )}
                      </div>
                    )}
                    {!watermark.enabled && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <span className="text-white font-medium text-sm bg-red-500/80 px-3 py-1 rounded-full">Watermark Disabled</span>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs mt-3 text-center">Preview is approximate. Actual watermark is applied by the server during image generation.</p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-2xl p-12 border border-gray-700 text-center">
                <RefreshCw className="w-8 h-8 text-gray-500 animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Loading watermark settings...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'promos' && (
          <div className="space-y-6">
            {/* Generate New Promo Code */}
            <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
              <h3 className="text-xl font-bold text-white mb-1">Generate Promo Link</h3>
              <p className="text-gray-400 text-sm mb-6">Create a single-use link that gives a new user 1 or 6 free month(s) ($0). Auto-charging at full price resumes the month after the free period ends.</p>
              
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-2">Select Plan</label>
                  <select
                    value={promoSelectedPlan}
                    onChange={(e) => setPromoSelectedPlan(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    data-testid="promo-plan-select"
                  >
                    <option value="">Choose a plan...</option>
                    {PLAN_OPTIONS.map(p => (
                      <option key={p.id} value={p.id}>{p.name} (${p.price}/mo)</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-2">Free Period</label>
                  <select
                    value={promoDuration}
                    onChange={(e) => setPromoDuration(Number(e.target.value))}
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    data-testid="promo-duration-select"
                  >
                    <option value={1}>1 month free</option>
                    <option value={6}>6 months free</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-2">Note (optional)</label>
                  <input
                    type="text"
                    value={promoNote}
                    onChange={(e) => setPromoNote(e.target.value)}
                    placeholder="e.g., For John's brand onboarding"
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    data-testid="promo-note-input"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={generatePromoCode}
                    disabled={!promoSelectedPlan || promoGenerating}
                    className="w-full bg-gradient-to-r from-rose-500 to-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:from-rose-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                    data-testid="generate-promo-btn"
                  >
                    {promoGenerating ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Tag className="w-4 h-4 mr-2" /> Generate Link</>
                    )}
                  </button>
                </div>
              </div>

              {/* Generated Link Display */}
              {promoGenerated && (
                <div className="mt-6 bg-gray-900 rounded-xl p-5 border border-emerald-500/30">
                  <div className="flex items-center mb-3">
                    <Check className="w-5 h-5 text-emerald-400 mr-2" />
                    <span className="text-emerald-400 font-semibold">Promo link generated!</span>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-sm text-gray-200 font-mono overflow-x-auto">
                      {promoGenerated.link}
                    </div>
                    <button
                      onClick={() => copyToClipboard(promoGenerated.link)}
                      className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-3 rounded-lg transition-colors flex items-center"
                      data-testid="copy-promo-link-btn"
                    >
                      {promoCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex items-center flex-wrap gap-4 text-sm text-gray-400">
                    <span>Plan: <strong className="text-white">{promoGenerated.plan_name}</strong></span>
                    <span>Free period: <strong className="text-emerald-400">{promoGenerated.duration_months === 6 ? '6 months' : '1 month'} free</strong></span>
                    <span>Code: <strong className="text-white font-mono">{promoGenerated.code}</strong></span>
                    <span>Regular price: <strong className="text-white">${promoGenerated.plan_price}/mo</strong></span>
                  </div>
                </div>
              )}
            </div>

            {/* Promo Code History */}
            <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Promo Code History</h3>
                <button onClick={fetchPromoCodes} className="text-gray-400 hover:text-white transition-colors">
                  <RefreshCw className={`w-5 h-5 ${promoLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              
              {promoLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 text-gray-500 animate-spin mx-auto mb-2" />
                  <p className="text-gray-400">Loading promo codes...</p>
                </div>
              ) : promoCodes.length === 0 ? (
                <div className="text-center py-8">
                  <Tag className="w-8 h-8 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No promo codes generated yet.</p>
                  <p className="text-gray-500 text-sm">Create your first one above!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Code</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Plan</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Free Period</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Note</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Created</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promoCodes.map((code) => (
                        <tr key={code.stripe_promo_id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                          <td className="py-3 px-4">
                            <span className="font-mono text-sm text-white bg-gray-900 px-2 py-1 rounded">{code.code}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-300">{code.plan_name} (${code.plan_price}/mo)</td>
                          <td className="py-3 px-4 text-sm">
                            {code.duration_months === 6 ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-300">6 months</span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/20 text-gray-300">1 month</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-400">{code.note || '-'}</td>
                          <td className="py-3 px-4">
                            {code.times_redeemed >= 1 ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                                Redeemed
                              </span>
                            ) : code.is_active ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400">
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                                Deactivated
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-400">
                            {code.created_at ? new Date(code.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => copyToClipboard(code.link)}
                                className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-700 transition-colors"
                                title="Copy link"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              {code.is_active && code.times_redeemed < 1 && (
                                <button
                                  onClick={() => deactivatePromoCode(code.stripe_promo_id)}
                                  className="text-gray-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-gray-700 transition-colors"
                                  title="Deactivate"
                                >
                                  <Ban className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* GEMINI ERRORS TAB */}
        {activeTab === 'gemini-errors' && (
          <div data-testid="gemini-errors-panel" className="space-y-6">
            {/* Header + refresh */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Gemini Upstream Errors</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Live log of failed try-on generation calls, classified by root cause.
                </p>
              </div>
              <button
                onClick={fetchGeminiErrors}
                disabled={geminiErrorsLoading}
                data-testid="gemini-errors-refresh-btn"
                className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 disabled:opacity-50 flex items-center"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${geminiErrorsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-5 rounded-xl border border-gray-200">
                <p className="text-xs uppercase tracking-wider text-gray-500">Total (recent)</p>
                <p className="text-3xl font-bold mt-2" data-testid="gemini-errors-total">
                  {geminiErrors?.count ?? 0}
                </p>
              </div>
              <div className="bg-red-50 p-5 rounded-xl border border-red-200">
                <p className="text-xs uppercase tracking-wider text-red-600">Balance / Credit</p>
                <p className="text-3xl font-bold text-red-700 mt-2" data-testid="gemini-errors-balance">
                  {geminiErrors?.by_classification?.balance ?? 0}
                </p>
                <p className="text-xs text-red-500 mt-1">Action: top up Universal Key</p>
              </div>
              <div className="bg-amber-50 p-5 rounded-xl border border-amber-200">
                <p className="text-xs uppercase tracking-wider text-amber-700">Rate Limit / Quota</p>
                <p className="text-3xl font-bold text-amber-700 mt-2" data-testid="gemini-errors-ratelimit">
                  {geminiErrors?.by_classification?.rate_limit ?? 0}
                </p>
                <p className="text-xs text-amber-600 mt-1">Action: upgrade tier / BYOK</p>
              </div>
              <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                <p className="text-xs uppercase tracking-wider text-gray-600">Other</p>
                <p className="text-3xl font-bold text-gray-700 mt-2" data-testid="gemini-errors-other">
                  {geminiErrors?.by_classification?.other ?? 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Inspect rows below</p>
              </div>
            </div>

            {/* Rows table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Recent error rows</h3>
              </div>
              {geminiErrorsLoading && (!geminiErrors || geminiErrors.rows.length === 0) ? (
                <div className="p-12 text-center text-gray-400">Loading…</div>
              ) : !geminiErrors || geminiErrors.rows.length === 0 ? (
                <div className="p-12 text-center text-gray-400" data-testid="gemini-errors-empty">
                  No errors recorded. Everything looks healthy.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">When</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User ID</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Attempt</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-testid="gemini-errors-rows">
                      {geminiErrors.rows.map((row) => {
                        const badge = row.classification === 'balance'
                          ? 'bg-red-100 text-red-700'
                          : row.classification === 'rate_limit'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-700';
                        const when = row.timestamp ? new Date(row.timestamp).toLocaleString() : '—';
                        return (
                          <tr key={row.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 text-gray-700 whitespace-nowrap">{when}</td>
                            <td className="px-6 py-3 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge}`}>
                                {row.classification}
                              </span>
                            </td>
                            <td className="px-6 py-3 text-gray-500 font-mono text-xs">
                              {row.user_id || <span className="italic text-gray-400">anonymous</span>}
                            </td>
                            <td className="px-6 py-3 text-gray-600">{row.attempt}</td>
                            <td className="px-6 py-3 text-gray-700">
                              <div className="max-w-md truncate" title={row.error}>{row.error}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
